import { useCallback } from 'react';

import { toastService } from '@thefarmersfront/kitchen';

import { useLoadingStore } from '~/store';

import { TOAST_MESSAGE } from '~/constants';

type TypeLoadingOption = boolean | { dimmed: boolean };

type AsyncHandlerOptions<T, E> = {
  /**
   * 로딩 표시 여부
   * @default true
   */
  showLoading?: TypeLoadingOption;
  /**
   * 성공 시 실행할 콜백 함수
   */
  onSuccess?: (response: T) => void | Promise<void>;
  /**
   * 실패 시 실행할 콜백 함수
   */
  onError?: (error: E) => void | Promise<void>;
};

/**
 * 비동기 함수 실행 핸들러
 */
const useAsyncHandler = () => {
  const { startLoading, endLoading } = useLoadingStore();

  const handleStartLoading = useCallback(
    (option: TypeLoadingOption) => {
      if (option === true) {
        startLoading();
      } else if (typeof option === 'object') {
        startLoading({ dimmed: option.dimmed });
      }
    },
    [startLoading]
  );

  const runAsync = useCallback(
    async <T, E = unknown>(
      asyncFunction: () => Promise<T>,
      { showLoading = true, onSuccess, onError }: AsyncHandlerOptions<T, E> = {}
    ) => {
      try {
        if (showLoading) {
          handleStartLoading(showLoading);
        }

        const response = await asyncFunction();

        await onSuccess?.(response);
      } catch (error) {
        await onError?.(error as E);
      } finally {
        if (showLoading) {
          endLoading();
        }
      }
    },
    [handleStartLoading, endLoading]
  );

  /**
   * 다운로드 전용 비동기 함수 실행 핸들러
   */
  const runDownloadAsync = useCallback(
    async <T, E = unknown>(
      asyncFunction: () => Promise<T>,
      {
        showLoading = true,
        onSuccess,
        onError,
        downloadingMessage = TOAST_MESSAGE.DOWNLOADING,
      }: AsyncHandlerOptions<T, E> & {
        /**
         * 다운로드 중 표시할 메시지
         * @default '다운로드 중..'
         */
        downloadingMessage?: string;
      } = {}
    ) => {
      let downloadingToastId: string | undefined;

      try {
        if (showLoading) {
          handleStartLoading(showLoading);
        }

        downloadingToastId = toastService.addToast({
          message: downloadingMessage,
          persist: true,
        });

        const response = await asyncFunction();

        await onSuccess?.(response);
      } catch (error) {
        await onError?.(error as E);
      } finally {
        if (showLoading) {
          endLoading();
        }

        if (downloadingToastId) {
          toastService.removeToast(downloadingToastId);
        }
      }
    },
    [handleStartLoading, endLoading]
  );

  /**
   * 여러 비동기 함수를 병렬로 실행하는 핸들러 (Promise.allSettled 사용)
   */
  const runAllSettled = useCallback(
    async <T = unknown, E = unknown>(
      asyncFunctions: (() => Promise<T>)[],
      { showLoading = true, onSuccess, onError }: AsyncHandlerOptions<PromiseSettledResult<T>[], E> = {}
    ) => {
      try {
        if (showLoading) {
          handleStartLoading(showLoading);
        }

        const results = await Promise.allSettled(asyncFunctions.map((fn) => fn()));

        await onSuccess?.(results);
      } catch (error) {
        await onError?.(error as E);
      } finally {
        if (showLoading) {
          endLoading();
        }
      }
    },
    [handleStartLoading, endLoading]
  );

  return { runAsync, runDownloadAsync, runAllSettled };
};

export default useAsyncHandler;
